import { useWeb3React } from "@web3-react/core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { metaMask, trustWallet, walletConnect } from "src/connectors";
import { isMobileDevice } from "src/utils/helpers";
import { WalletType } from "../constants/enums";
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react'
import {optimismConfig} from "../constants/optimism";

const WalletContext = createContext();
WalletContext.displayName = "Wallet Context";

function WalletProvider({ children }) {
  const [walletType, setWalletType] = useState(WalletType.None);
  const [walletAddress, setWalletAddress] = useState("");
  const location = useLocation();
  const fromMetamask = new URLSearchParams(location.search).get("metamask");

  const metamaskConfig = useMemo(() => {
    return location.pathname === '/vaults/optimism' ? optimismConfig : {}
  }, [location])


  const {wallet, connected} = useSolanaWallet()

  useEffect(() => {
    const account = wallet?.adapter.publicKey || ''
    setWalletAddress(account.toString())
    unsetProvider()
    setWalletType(WalletType.None)
  }, [wallet, connected]);

  const { account, isActive, provider, chainId } = useWeb3React();

  const setProvider = (provider) => {
    sessionStorage.setItem('connected', 'true')
    sessionStorage.setItem('provider', provider)
  }

  const unsetProvider = () => {
    sessionStorage.removeItem('connected')
    sessionStorage.removeItem('provider')
  }

  const connect = useCallback((walletType) => {
    if (walletType === WalletType.Metamask) {
      if (isMobileDevice()) {
        const metamaskAppDeepLink =
          "https://metamask.app.link/dapp/" +
          process.env.REACT_APP_DAPP_URL +
          "?metamask=true";
        window.location.href = metamaskAppDeepLink;
        return;
      } else {
        metaMask
          .activate(metamaskConfig)
          .then(() => {
            setProvider(WalletType.Metamask)
          })
          .catch((e) => console.log(`Error connecting with Metamask: ${e}`));
      }
    }
    if (walletType === WalletType.WalletConnect) {
      walletConnect
        .activate()
        .then(() => {
          setProvider(WalletType.WalletConnect)
        })
        .catch((e) =>
          console.log(`Error connecting with Wallet Connect: ${e}`)
        );
    }
    if (walletType === WalletType.TrustWallet) {
      trustWallet
        .activate()
        .then(() => {
          setProvider(WalletType.TrustWallet)
        })
        .catch((e) => console.log(`Error connecting with Trust Wallet: ${e}`));
    }
    setWalletType(walletType);
  }, [metamaskConfig]);

  const disconnect = useCallback(() => {
    if (walletType === WalletType.None) setWalletAddress("");
    if (walletType === WalletType.Metamask) {
      if (isMobileDevice()) {
        setWalletAddress("");
      } else {
        metaMask.resetState();
      }
    } else if (walletType === WalletType.WalletConnect) {
      walletConnect.resetState();
    } else if (walletType === WalletType.TrustWallet) {
      trustWallet.resetState();
    }
    setWalletType(WalletType.None);
    unsetProvider()
  }, [walletType]);

  useEffect(() => {
    (async () => {
      if (fromMetamask) {
        if (!window.ethereum) {
          window.location.href = "https://metamask.io/download/";
          return;
        }
        const accounts = await window.ethereum.request({
          method: "eth_requestAccounts",
        });
        setWalletAddress(accounts[0]);
        setWalletType(WalletType.Metamask);
      }
    })();
  }, [fromMetamask]);

  useEffect(() => {
    setWalletAddress(account);
  }, [account]);

  useEffect(() => {
    const connectedProvider = window.sessionStorage.getItem('provider')
    if(!connectedProvider) return

    if(connectedProvider === WalletType.Metamask) {
      setWalletType(WalletType.Metamask)
    } else if(connectedProvider === WalletType.WalletConnect) {
      setWalletType(WalletType.WalletConnect)
    } else if(connectedProvider === WalletType.TrustWallet) {
      setWalletType(WalletType.TrustWallet)
    }
  }, [])

  useEffect(() => {
    const isConnected = window.sessionStorage.getItem('connected')
    const reconnect = async () => {
      const connectedProvider = sessionStorage.getItem('provider')
      if (connectedProvider === WalletType.Metamask) {
        await metaMask.connectEagerly()
      } else if (connectedProvider === WalletType.WalletConnect) {
        await walletConnect.connectEagerly()
      } else if(connectedProvider === WalletType.TrustWallet) {
        await trustWallet.connectEagerly()
      }
    }
    if (isConnected === 'true') {
      reconnect()
    }
  }, [])

  const value = useMemo(
    () => ({
      walletType,
      walletAddress,
      isActive,
      provider,
      setWalletAddress,
      connect,
      disconnect,
      chainId
    }),
    [walletType, walletAddress, isActive, provider, connect, disconnect, chainId]
  );


  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
}

const useWallet = () => {
  const value = useContext(WalletContext);
  if (!value)
    throw new Error("useWallet hook must be used within WalletProvider");
  return value;
};

export { useWallet, WalletProvider };
